import type * as maplibregl from "maplibre-gl";
import type { DecorRenderStats } from "./decorLayer";

/** Une mesure du banc d'essai : un geste de caméra, joué avec une partie des couches masquée ou non. */
export interface RenderBenchmarkResult {
  /** D'où le geste est joué : vue de départ, d'ensemble, rapprochée, zone la plus chargée. */
  view: string;
  scenario: string;
  variant: string;
  /** Images par seconde, médiane et dixième le plus lent : ce dernier dit si le geste reste fluide. */
  fpsMedian: number;
  fpsLow: number;
  worstFrameMs: number;
  /** Images de plus de 50 ms : en deçà de 20 i/s, l'œil voit l'à-coup. */
  slowFrames: number;
  /** Durée réelle de la mesure, pour ramener à-coups et tâches longues à la seconde. */
  durationMs?: number;
  longTasks: number;
  longTasksMs: number;
  /** Temps moyen du calque de décor par image, quand il existe. */
  decorMs: number | null;
  /** Ce même temps, étape par étape, quand le calque le détaille. */
  decorPhases: Record<string, number> | null;
  /** Le navigateur a ralenti l'affichage pendant la mesure (fenêtre masquée) : les chiffres ne valent rien. */
  throttled: boolean;
  /** Les compteurs du décor relevés à la fin de la mesure : volumes dessinés, objets revus. */
  decorSample: DecorRenderStats | null;
  /** Premier rapprochement : temps, une fois arrivé, jusqu'à ce que le fond de carte soit entièrement affiché. */
  settleMs?: number | null;
  /** Passages dont la mesure est la médiane : un seul passage varie trop d'un essai à l'autre. */
  runs?: number;
}

export const VIEW_START = "Vue de départ";
export const VIEW_OVERVIEW = "Vue d'ensemble";
export const VIEW_CLOSE = "Vue rapprochée";
export const VIEW_DENSE = "Zone la plus chargée";
export const VIEW_CLOSEST = "Au plus près";
export const FIRST_APPROACH = "Premier rapprochement";

/**
 * Les points de vue où jouer les gestes, au-delà de la vue de départ : choisis par l'adaptateur, qui
 * connaît le contenu de la carte. `null` quand l'un d'eux n'apporterait rien (carte minuscule…).
 */
export interface BenchmarkViews {
  /** Toute la carte à l'écran. */
  overview: maplibregl.CameraOptions | null;
  /** Le centre de la vue de départ, au plus près qu'un visiteur puisse zoomer. */
  close: maplibregl.CameraOptions | null;
  /** L'endroit le plus chargé en objets 3D, au même zoom rapproché. */
  dense: maplibregl.CameraOptions | null;
  /** Au zoom le plus fort permis, là où les sols sont habillés : herbe et cailloux n'apparaissent que de très près. */
  closest?: maplibregl.CameraOptions | null;
  /** D'un bord à l'autre de la carte, au zoom de la vue de départ. */
  traverse: { from: [number, number]; to: [number, number] } | null;
  /** Inclinaison maximale permise ; zéro sans 3D. */
  maxPitch: number;
}

/**
 * L'arrivée sur la carte, relevée une fois à l'ouverture : le temps de chargement, puis les premières
 * secondes d'affichage, pendant lesquelles le décor se prépare (et le vol d'arrivée se joue, s'il est actif).
 */
export interface ArrivalMeasure {
  /** De la création de la carte à son premier affichage complet. */
  loadMs: number;
  window: RenderBenchmarkResult;
  /** Le vol d'arrivée s'est joué pendant la fenêtre mesurée. */
  flight: boolean;
  /** Ouverte en édition : un visiteur ne la voit pas tout à fait ainsi (ni animations ni vol). */
  editor: boolean;
  /** Rejouée depuis l'éditeur (voir `createTokaMap`) : document déjà en mémoire, ressources souvent en cache. */
  estimate?: boolean;
  /** `loadMs` court depuis l'ouverture de la page, et non depuis la création de la carte. */
  fromPageStart?: boolean;
  /** Ressources du chargement servies par le cache du navigateur, ou téléchargées ; `null` si rien n'est connu. */
  cache: ResourceCacheCount | null;
}

export interface ResourceCacheCount {
  cached: number;
  fetched: number;
}

/**
 * Compte ce qui est venu du cache (`transferSize` nul) et ce qui a été téléchargé. Une ressource d'un
 * autre site qui ne publie pas ses temps a toutes ses tailles nulles : on ne sait rien d'elle.
 */
export function resourceCacheCount(entries: Pick<PerformanceResourceTiming, "transferSize" | "decodedBodySize">[]): ResourceCacheCount | null {
  let cached = 0;
  let fetched = 0;
  for (const entry of entries) {
    if (entry.transferSize === 0 && entry.decodedBodySize === 0) continue;
    if (entry.transferSize === 0) cached++;
    else fetched++;
  }
  return cached + fetched ? { cached, fetched } : null;
}

/** Durée des premières secondes mesurées à l'arrivée, vol d'arrivée en plus. */
export const ARRIVAL_WINDOW_MS = 5000;
export const ARRIVAL_SCENARIO = "Arrivée";

/** Une fenêtre masquée ou en arrière-plan n'a que quelques images par seconde : rien à mesurer. */
export class RenderBenchmarkThrottledError extends Error {
  constructor() {
    super("La fenêtre n'est pas au premier plan : le navigateur ralentit l'affichage et la mesure serait faussée.");
  }
}

export interface RenderBenchmarkOptions {
  /** Durée de chaque mesure. */
  durationMs?: number;
  onProgress?: (done: number, total: number, label: string) => void;
  /** Sans eux, seuls les gestes autour de la vue de départ sont joués. */
  views?: BenchmarkViews;
  /** Un geste qui n'a pas pu être mesuré : le banc passe au suivant plutôt que de s'arrêter. */
  onRunFailed?: (label: string, error: unknown) => void;
  /** Appelées avant la première mesure et après la dernière, même interrompue. */
  onStart?: () => void;
  onEnd?: () => void;
  /** Relève les compteurs du décor à la fin de chaque mesure. */
  sampleDecor?: () => DecorRenderStats | null;
  signal?: AbortSignal;
}

const DEFAULT_DURATION_MS = 3000;
/** Un parcours (grand zoom, traversée, premier rapprochement) prend plus de temps qu'un geste sur place. */
const JOURNEY_DURATION_MS = 4000;
/** Passages de chaque geste, dont on garde la médiane. */
const REPEATS = 2;
/** Avant de mesurer, la carte finit de se charger, sans attendre au-delà. */
const SETTLE_MAX_MS = 6000;
const SETTLE_PAUSE_MS = 300;
/** Au-delà, le fond de carte n'a toujours pas fini de s'afficher après le premier rapprochement. */
const FIRST_APPROACH_SETTLE_MAX_MS = 15_000;
const SLOW_FRAME_MS = 50;
/** En deçà, l'écran n'est pas servi normalement : onglet masqué, fenêtre recouverte ou réduite. */
const MIN_SCREEN_FPS = 20;
/** En deçà, une cadence basse alors que l'image se dessine vite trahit une fenêtre que le navigateur ralentit. */
const SUSPECT_FPS = 50;

/**
 * Le temps d'une image dessinée sur commande, GPU compris : ce que la carte coûte vraiment, quelle que
 * soit la cadence que le navigateur lui accorde. Infini quand on ne peut pas le mesurer.
 */
function forcedRenderMs(map: maplibregl.Map): number {
  const gl = (map as unknown as { painter?: { context?: { gl?: WebGLRenderingContext } } }).painter?.context?.gl;
  if (!gl) return Infinity;
  const began = performance.now();
  for (let i = 0; i < 3; i++) {
    map.redraw();
    gl.finish();
  }
  return (performance.now() - began) / 3;
}

/** En deçà de cette part du temps occupée à calculer, une page lente attend : c'est le navigateur qui la bride. */
const BUSY_SHARE_MIN = 0.35;

/**
 * Faussée : une cadence basse alors que la page attend la plupart du temps (`busyShare`, part du
 * temps passée à calculer) et qu'une image dessinée sur commande, GPU compris, coûte peu. Une carte
 * vraiment lourde, elle, calcule sans arrêt ou dessine lentement.
 */
export function looksThrottled(fpsMedian: number, renderMs: number, busyShare: number): boolean {
  return fpsMedian > 0 && fpsMedian < SUSPECT_FPS && busyShare < BUSY_SHARE_MIN && renderMs * 2 < 1000 / fpsMedian;
}

const MOVEMENT_PHASES = /^(move|zoom|rotate|pitch|drag)(start|end)$/;

/**
 * Pendant un geste, un seul début et une seule fin de mouvement, comme un vrai glisser : `jumpTo`
 * en émet à chaque image, et les traitements de fin de mouvement, joués à chaque fois, chargeraient
 * la mesure de ce qu'un visiteur ne paie qu'une fois. Rend de quoi rétablir et clore le mouvement.
 */
export function holdMovementPhases(map: maplibregl.Map): () => void {
  const target = map as unknown as { fire: (event: unknown, ...rest: unknown[]) => unknown };
  const fire = target.fire;
  const started = new Set<string>();
  target.fire = function (this: unknown, event: unknown, ...rest: unknown[]) {
    const type = typeof event === "string" ? event : ((event as { type?: string }).type ?? "");
    const phase = MOVEMENT_PHASES.exec(type);
    if (phase?.[2] === "end") return this;
    if (phase?.[2] === "start") {
      if (started.has(type)) return this;
      started.add(type);
    }
    return fire.call(this, event, ...rest);
  };
  return () => {
    target.fire = fire;
    for (const type of started) fire.call(map, type.replace(/start$/, "end"));
  };
}

const EARTH_CIRCUMFERENCE_M = 40_075_016.686;
const METERS_PER_DEGREE = 111_320;

function metersPerPixelAt(start: maplibregl.CameraOptions): number {
  const [, lat] = toLngLat(start.center);
  return (EARTH_CIRCUMFERENCE_M * Math.cos((lat * Math.PI) / 180)) / (512 * 2 ** (start.zoom ?? 16));
}

/**
 * Le centre de la carte à l'instant `t` (de 0 à 1) d'un tour complet de rayon `radiusMeters`, parti
 * du centre de départ et qui y revient. Calculé depuis la vue de départ et jamais depuis la vue en
 * cours : repartir de la carte qui bouge déjà la ferait s'emballer par à-coups.
 */
export function circlingCenter(start: { lng: number; lat: number }, radiusMeters: number, t: number): [number, number] {
  const angle = t * Math.PI * 2;
  const east = Math.sin(angle) * radiusMeters;
  const north = (1 - Math.cos(angle)) * radiusMeters;
  return [start.lng + east / (METERS_PER_DEGREE * Math.cos((start.lat * Math.PI) / 180)), start.lat + north / METERS_PER_DEGREE];
}

type Gesture = ((map: maplibregl.Map, progress: number) => void) | null;

const orbit = (start: maplibregl.CameraOptions): Gesture => (map, t) => {
  const radiusMeters = Math.min(map.getCanvas().clientWidth, map.getCanvas().clientHeight) * 0.35 * metersPerPixelAt(start);
  const [lng, lat] = toLngLat(start.center);
  map.jumpTo({ center: circlingCenter({ lng, lat }, radiusMeters, t) });
};
const zoomAround = (start: maplibregl.CameraOptions): Gesture => (map, t) => map.jumpTo({ zoom: (start.zoom ?? 16) + Math.sin(t * Math.PI * 2) * 1.2 });
const rotate = (start: maplibregl.CameraOptions): Gesture => (map, t) => map.jumpTo({ bearing: (start.bearing ?? 0) + t * 120 });
const there = (t: number) => 0.5 - 0.5 * Math.cos(t * Math.PI * 2);
const tilt = (start: maplibregl.CameraOptions, maxPitch: number): Gesture => (map, t) => map.jumpTo({ pitch: (start.pitch ?? 0) + (maxPitch - (start.pitch ?? 0)) * there(t) });
/** De `from` à `to` puis retour, en zoom et en position : comme un visiteur qui plonge vers un lieu. */
const dive = (from: maplibregl.CameraOptions, to: maplibregl.CameraOptions): Gesture => (map, t) => {
  const k = there(t);
  const [fromLng, fromLat] = toLngLat(from.center);
  const [toLng, toLat] = toLngLat(to.center);
  map.jumpTo({ center: [fromLng + (toLng - fromLng) * k, fromLat + (toLat - fromLat) * k], zoom: (from.zoom ?? 16) + ((to.zoom ?? 16) - (from.zoom ?? 16)) * k });
};
/** Aller simple, en douceur : le premier rapprochement ne revient pas sur ses pas. */
const approach = (from: maplibregl.CameraOptions, to: maplibregl.CameraOptions): Gesture => (map, t) => {
  const k = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
  const [fromLng, fromLat] = toLngLat(from.center);
  const [toLng, toLat] = toLngLat(to.center);
  map.jumpTo({ center: [fromLng + (toLng - fromLng) * k, fromLat + (toLat - fromLat) * k], zoom: (from.zoom ?? 16) + ((to.zoom ?? 16) - (from.zoom ?? 16)) * k });
};
const traverse = (from: [number, number], to: [number, number]): Gesture => (map, t) => map.jumpTo({ center: [from[0] + (to[0] - from[0]) * t, from[1] + (to[1] - from[1]) * t] });

const METERS_PER_DEGREE_LAT = 111_320;

/**
 * Le centre de la case de `cellMeters` qui contient le plus de points, s'il y en a au moins
 * `minCount` : l'endroit où la carte a le plus d'objets à dessiner.
 */
export function densestPoint(points: [number, number][], cellMeters: number, minCount: number): [number, number] | null {
  if (!points.length) return null;
  const latitude = points.reduce((sum, [, lat]) => sum + lat, 0) / points.length;
  const lngStep = cellMeters / (METERS_PER_DEGREE_LAT * Math.cos((latitude * Math.PI) / 180));
  const latStep = cellMeters / METERS_PER_DEGREE_LAT;
  const cells = new Map<string, [number, number][]>();
  for (const point of points) {
    const key = `${Math.floor(point[0] / lngStep)}:${Math.floor(point[1] / latStep)}`;
    let cell = cells.get(key);
    if (!cell) cells.set(key, (cell = []));
    cell.push(point);
  }
  let best: [number, number][] = [];
  for (const cell of cells.values()) if (cell.length > best.length) best = cell;
  if (best.length < minCount) return null;
  return [best.reduce((sum, [lng]) => sum + lng, 0) / best.length, best.reduce((sum, [, lat]) => sum + lat, 0) / best.length];
}

function toLngLat(center: maplibregl.CameraOptions["center"]): [number, number] {
  if (Array.isArray(center)) return [center[0], center[1]];
  const point = center as { lng: number; lat: number; lon?: number };
  return [point.lng ?? point.lon ?? 0, point.lat];
}

/** Un geste à mesurer : d'où il part, ce qu'il fait, combien de fois. */
interface BenchmarkRun {
  view: string;
  scenario: string;
  variant: string;
  camera: maplibregl.CameraOptions;
  gesture: Gesture;
  durationMs: number;
  repeat: number;
  hidden: string[];
  /** Le temps d'affichage complet du fond de carte, une fois le geste fini, compte aussi. */
  measureSettle?: boolean;
}

/**
 * Les gestes joués, dans l'ordre : le premier rapprochement en tête, tant que la carte n'a encore
 * rien vu de près — c'est ce que vit un visiteur la première fois qu'il zoome.
 */
export function plannedRuns(start: maplibregl.CameraOptions, views: BenchmarkViews | undefined, durationMs: number): BenchmarkRun[] {
  const runs: BenchmarkRun[] = [];
  const add = (view: string, scenario: string, camera: maplibregl.CameraOptions, gesture: Gesture, extra: Partial<BenchmarkRun> = {}) =>
    runs.push({ view, scenario, variant: "tout", camera, gesture, durationMs, repeat: REPEATS, hidden: [], ...extra });
  add(VIEW_START, "Immobile", start, null);
  const closeTarget = views?.dense ?? views?.close ?? null;
  if (closeTarget) add(VIEW_START, FIRST_APPROACH, start, approach(start, closeTarget), { durationMs: JOURNEY_DURATION_MS, repeat: 1, measureSettle: true });
  add(VIEW_START, "Déplacement", start, orbit(start));
  add(VIEW_START, "Zoom", start, zoomAround(start));
  add(VIEW_START, "Rotation", start, rotate(start));
  const maxPitch = views?.maxPitch ?? 0;
  if (maxPitch > (start.pitch ?? 0) + 5) {
    add(VIEW_START, "Inclinaison", start, tilt(start, maxPitch));
    const tilted = { ...start, pitch: maxPitch };
    add(VIEW_START, "Déplacement incliné", tilted, orbit(tilted));
  }
  if (views?.overview) {
    add(VIEW_OVERVIEW, "Déplacement", views.overview, orbit(views.overview));
    add(VIEW_OVERVIEW, "Rotation", views.overview, rotate(views.overview));
  }
  if (views?.close) {
    add(VIEW_CLOSE, "Déplacement", views.close, orbit(views.close));
    add(VIEW_CLOSE, "Rotation", views.close, rotate(views.close));
  }
  if (views?.dense) add(VIEW_DENSE, "Déplacement", views.dense, orbit(views.dense));
  if (views?.closest) {
    add(VIEW_CLOSEST, "Déplacement", views.closest, orbit(views.closest));
    add(VIEW_CLOSEST, "Rotation", views.closest, rotate(views.closest));
  }
  if (views?.overview && closeTarget) add(VIEW_OVERVIEW, "Grand zoom", views.overview, dive(views.overview, closeTarget), { durationMs: JOURNEY_DURATION_MS });
  if (views?.traverse) {
    const from = { ...start, center: views.traverse.from };
    add(VIEW_START, "Traversée", from, traverse(views.traverse.from, views.traverse.to), { durationMs: JOURNEY_DURATION_MS });
  }
  return runs;
}

const median = (values: number[]) => {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = sorted.length / 2;
  return sorted.length % 2 ? sorted[Math.floor(middle)]! : (sorted[middle - 1]! + sorted[middle]!) / 2;
};

/** Plusieurs passages d'un même geste en un : la médiane de chaque chiffre, sur les passages qu'aucune fenêtre ralentie n'a faussés. */
export function mergeRuns(results: RenderBenchmarkResult[]): RenderBenchmarkResult {
  const kept = results.filter((result) => !result.throttled);
  const pool = kept.length ? kept : results;
  const first = pool[0]!;
  if (pool.length === 1) return { ...first, runs: 1 };
  const of = (pick: (result: RenderBenchmarkResult) => number) => median(pool.map(pick));
  const decor = pool.every((result) => result.decorMs !== null) ? of((result) => result.decorMs!) : null;
  return {
    ...first,
    fpsMedian: of((result) => result.fpsMedian),
    fpsLow: of((result) => result.fpsLow),
    worstFrameMs: of((result) => result.worstFrameMs),
    slowFrames: Math.round(of((result) => result.slowFrames)),
    durationMs: of((result) => result.durationMs ?? 0),
    longTasks: Math.round(of((result) => result.longTasks)),
    longTasksMs: of((result) => result.longTasksMs),
    decorMs: decor,
    decorSample: pool.at(-1)!.decorSample,
    runs: pool.length,
  };
}

const isOwnLayer = (id: string) => /^(shapes|line-walls|tokamap|shape-|contact|editing|selection|draft|snap|route|highlight|user|edit|add-vertex|rotate|badge|marker|pathfinding|remote)/.test(id);

/** Les couches que l'on masque tour à tour, pour voir ce que chacune coûte. */
function layerGroups(map: maplibregl.Map): Record<string, string[]> {
  const order = map.getStyle().layers.map((layer) => layer.id).concat(["tokamap-decor", "shape-roof-images"].filter((id) => map.getLayer(id)));
  const unique = [...new Set(order)];
  return {
    "sans décor 3D": unique.filter((id) => id === "tokamap-decor"),
    "sans volumes": unique.filter((id) => map.getLayer(id)?.type === "fill-extrusion"),
    "sans textures de sol": unique.filter((id) => /surface/.test(id)),
    "sans fond OSM": unique.filter((id) => !isOwnLayer(id)),
  };
}

function quantile(sorted: number[], share: number): number {
  return sorted.length ? sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * share))]! : 0;
}

/**
 * Mesure ce que coûtent les gestes d'un visiteur sur la carte telle qu'elle est : autour de la vue de
 * départ, de loin, de près et là où elle est la plus chargée, chaque geste deux fois. Le plus lent est
 * ensuite rejoué avec chaque groupe de couches masqué, pour désigner ce qui coûte. La caméra et les
 * couches sont rendues dans leur état d'origine, même si la mesure est interrompue.
 */
export async function runRenderBenchmark(map: maplibregl.Map, options: RenderBenchmarkOptions = {}): Promise<RenderBenchmarkResult[]> {
  const duration = options.durationMs ?? DEFAULT_DURATION_MS;
  const start: maplibregl.CameraOptions = { center: map.getCenter(), zoom: map.getZoom(), bearing: map.getBearing(), pitch: map.getPitch() };
  const groups = Object.entries(layerGroups(map)).filter(([, ids]) => ids.length);
  const visibility = new Map<string, "visible" | "none">();
  for (const [, ids] of groups) for (const id of ids) visibility.set(id, map.getLayoutProperty(id, "visibility") === "none" ? "none" : "visible");

  const runs = plannedRuns(start, options.views, duration);
  const total = runs.reduce((sum, run) => sum + run.repeat, 0) + groups.length;
  let done = 0;

  if ((await screenFps(1000)) < MIN_SCREEN_FPS) throw new RenderBenchmarkThrottledError();

  const decorLayer = decorImplementation(map);
  const results: RenderBenchmarkResult[] = [];
  const play = async (run: BenchmarkRun): Promise<RenderBenchmarkResult> => {
    options.onProgress?.(done++, total, `${run.view} — ${run.scenario}${run.variant === "tout" ? "" : ` — ${run.variant}`}`);
    for (const id of run.hidden) map.setLayoutProperty(id, "visibility", "none");
    try {
      map.jumpTo(run.camera);
      if (!run.measureSettle) await settle(map, SETTLE_MAX_MS, options.signal);
      const result = await measure(map, run.view, run.scenario, run.variant, run.durationMs, run.gesture, decorLayer, options.signal);
      const settleMs = run.measureSettle ? await settle(map, FIRST_APPROACH_SETTLE_MAX_MS, options.signal) : undefined;
      return { ...result, decorSample: options.sampleDecor?.() ?? null, ...(settleMs === undefined ? {} : { settleMs }) };
    } finally {
      for (const id of run.hidden) map.setLayoutProperty(id, "visibility", visibility.get(id) ?? "visible");
    }
  };

  options.onStart?.();
  try {
    const played: { run: BenchmarkRun; result: RenderBenchmarkResult }[] = [];
    const attempt = async (run: BenchmarkRun): Promise<RenderBenchmarkResult | null> => {
      try {
        return await play(run);
      } catch (error) {
        options.onRunFailed?.(`${run.view} — ${run.scenario}${run.variant === "tout" ? "" : ` — ${run.variant}`}`, error);
        return null;
      }
    };
    for (const run of runs) {
      const passes: RenderBenchmarkResult[] = [];
      for (let pass = 0; pass < run.repeat && !options.signal?.aborted; pass++) {
        const result = await attempt(run);
        if (!result) {
          done += run.repeat - pass - 1;
          break;
        }
        passes.push(result);
      }
      if (options.signal?.aborted) break;
      if (!passes.length) continue;
      const merged = mergeRuns(passes);
      results.push(merged);
      played.push({ run, result: merged });
    }
    // Le geste le plus saccadé, rejoué avec chaque groupe de couches masqué : c'est là que le gain se voit.
    const slowest = played
      .filter(({ run, result }) => run.gesture && !run.measureSettle && !result.throttled)
      .sort((a, b) => a.result.fpsLow - b.result.fpsLow)[0]?.run;
    for (const [variant, hidden] of slowest ? groups : []) {
      if (options.signal?.aborted) break;
      const result = await attempt({ ...slowest!, variant, hidden, repeat: 1 });
      if (result) results.push(result);
    }
  } finally {
    for (const [id, value] of visibility) if (map.getLayer(id)) map.setLayoutProperty(id, "visibility", value);
    options.onEnd?.();
    map.jumpTo(start);
  }
  options.onProgress?.(total, total, "Terminé");
  return results;
}

/** Le nombre de mesures que `runRenderBenchmark` va jouer depuis la vue affichée, pour annoncer la progression d'avance. */
export function renderBenchmarkSteps(map: maplibregl.Map, views: BenchmarkViews | undefined): number {
  const start: maplibregl.CameraOptions = { center: map.getCenter(), zoom: map.getZoom(), bearing: map.getBearing(), pitch: map.getPitch() };
  const groups = Object.values(layerGroups(map)).filter((ids) => ids.length).length;
  return plannedRuns(start, views, DEFAULT_DURATION_MS).reduce((sum, run) => sum + run.repeat, 0) + groups;
}

/**
 * Attend que la carte ait fini de charger ce qu'elle montre (tuiles, images), sans dépasser `maxMs` ;
 * rend le temps écoulé. Une carte animée se redessine sans cesse : seules les sources comptent.
 */
function settle(map: maplibregl.Map, maxMs: number, signal: AbortSignal | undefined): Promise<number> {
  const began = performance.now();
  return new Promise((resolve) => {
    const check = () => {
      const elapsed = performance.now() - began;
      if (signal?.aborted || elapsed >= maxMs || (map.loaded() && map.areTilesLoaded() && !map.isMoving())) {
        setTimeout(() => resolve(elapsed), SETTLE_PAUSE_MS);
        return;
      }
      setTimeout(check, 50);
    };
    check();
  });
}

type DecorImplementation = { render: (...args: unknown[]) => void };

function decorImplementation(map: maplibregl.Map): DecorImplementation | undefined {
  return (map.getLayer("tokamap-decor") as unknown as { implementation?: DecorImplementation } | undefined)?.implementation;
}

/**
 * Les premières secondes après le chargement, telles qu'elles se déroulent : la caméra n'est pas
 * touchée (un vol d'arrivée en cours se poursuit), la carte est seulement redessinée à chaque image.
 */
export async function measureArrival(map: maplibregl.Map, durationMs: number): Promise<RenderBenchmarkResult> {
  const result = await measure(map, VIEW_START, ARRIVAL_SCENARIO, "tout", durationMs, null, decorImplementation(map), undefined);
  return { ...result, decorSample: null };
}

async function measure(
  map: maplibregl.Map,
  view: string,
  scenario: string,
  variant: string,
  duration: number,
  gesture: Gesture,
  decorLayer: DecorImplementation | undefined,
  signal: AbortSignal | undefined,
): Promise<Omit<RenderBenchmarkResult, "decorSample">> {
  const stamps: number[] = [];
  const onRender = () => stamps.push(performance.now());
  const longTasks: number[] = [];
  const observer = typeof PerformanceObserver !== "undefined" && PerformanceObserver.supportedEntryTypes?.includes("longtask")
    ? new PerformanceObserver((list) => list.getEntries().forEach((entry) => longTasks.push(entry.duration)))
    : null;
  observer?.observe({ type: "longtask" });

  let decorTotal = 0;
  let decorFrames = 0;
  const originalRender = decorLayer?.render;
  const decorStats = decorLayer as unknown as { getStats?: () => { phases: Record<string, number> } } | undefined;
  const phaseTotals: Record<string, number> = {};
  let phaseSamples = 0;
  const samplePhases = () => {
    const phases = decorStats?.getStats?.().phases;
    if (!phases) return;
    for (const [name, value] of Object.entries(phases)) phaseTotals[name] = (phaseTotals[name] ?? 0) + value;
    phaseSamples++;
  };
  const sampler = decorStats?.getStats ? setInterval(samplePhases, 1000) : null;
  if (decorLayer && originalRender) {
    decorLayer.render = function (this: unknown, ...args: unknown[]) {
      const began = performance.now();
      originalRender.apply(this, args);
      decorTotal += performance.now() - began;
      decorFrames++;
    };
  }

  map.on("render", onRender);
  const releaseMovement = gesture ? holdMovementPhases(map) : null;
  const began = performance.now();
  // Temps passé à calculer dans chaque image, depuis son début : ce qui distingue une carte lourde d'une fenêtre bridée.
  let busy = 0;
  try {
    await new Promise<void>((resolve, reject) => {
      const step = (frameStart: number) => {
        const elapsed = performance.now() - began;
        if (elapsed >= duration || signal?.aborted) return resolve();
        // Une erreur dans le geste arrêterait la boucle sans jamais rendre la main : la mesure échoue franchement.
        try {
          if (gesture) gesture(map, elapsed / duration);
          else map.triggerRepaint();
        } catch (error) {
          reject(error);
          return;
        }
        busy += Math.max(0, performance.now() - frameStart);
        requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    });
  } finally {
    releaseMovement?.();
    map.off("render", onRender);
    observer?.disconnect();
    if (sampler !== null) clearInterval(sampler);
    if (decorLayer && originalRender) decorLayer.render = originalRender;
  }

  const gaps = stamps.slice(1).map((stamp, index) => stamp - stamps[index]!).sort((a, b) => a - b);
  const fpsMedian = gaps.length ? 1000 / quantile(gaps, 0.5) : 0;
  const measuredMs = performance.now() - began;
  const longTasksMs = longTasks.reduce((sum, value) => sum + value, 0);
  const busyShare = Math.min(1, (busy + longTasksMs) / measuredMs);
  return {
    view,
    scenario,
    variant,
    fpsMedian,
    fpsLow: gaps.length ? 1000 / quantile(gaps, 0.9) : 0,
    worstFrameMs: gaps.at(-1) ?? 0,
    slowFrames: gaps.filter((gap) => gap > SLOW_FRAME_MS).length,
    durationMs: measuredMs,
    longTasks: longTasks.length,
    longTasksMs,
    decorMs: decorLayer ? (decorFrames ? decorTotal / decorFrames : 0) : null,
    decorPhases: phaseSamples ? Object.fromEntries(Object.entries(phaseTotals).map(([name, total]) => [name, total / phaseSamples])) : null,
    throttled: looksThrottled(fpsMedian, forcedRenderMs(map), busyShare),
  };
}

function screenFps(ms: number): Promise<number> {
  return new Promise((resolve) => {
    let frames = 0;
    const began = performance.now();
    const step = () => {
      frames++;
      const elapsed = performance.now() - began;
      if (elapsed >= ms) resolve((frames * 1000) / elapsed);
      else requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  });
}

/** Le tableau des résultats en texte, pour le coller dans un ticket ou une discussion. */
export function formatRenderBenchmark(results: RenderBenchmarkResult[]): string {
  const header = "Vue | Geste | Couches | i/s médian | i/s 10 % lents | pire image | images > 50 ms | tâches longues | décor / image | détail du décor (ms)";
  const lines = results.map((result) => [
    result.view,
    result.settleMs == null ? result.scenario : `${result.scenario} (fond affiché en ${(result.settleMs / 1000).toFixed(1)} s)`,
    result.variant,
    result.fpsMedian.toFixed(0),
    result.fpsLow.toFixed(0),
    `${result.worstFrameMs.toFixed(0)} ms`,
    String(result.slowFrames),
    `${result.longTasks} (${result.longTasksMs.toFixed(0)} ms)`,
    result.decorMs === null ? "—" : `${result.decorMs.toFixed(1)} ms`,
    result.decorPhases ? Object.entries(result.decorPhases).map(([name, value]) => `${name} ${value.toFixed(1)}`).join(", ") : "",
  ].join(" | ") + (result.throttled ? " | faussé (fenêtre masquée)" : ""));
  return [header, ...lines].join("\n");
}
